// Recursive, collapsible knowledge-tree renderer. Used by both the global view
// (participant counts) and the personal view (with `user_level` annotations).
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "@/components/ui";
import type { TreeNode } from "@/api/stats-types";
import { pct, pct100 } from "./format";

const KIND_COLOR: Record<string, string> = {
  subject: "bg-brand-100 text-brand-700",
  area: "bg-violet-100 text-violet-700",
  topic: "bg-black/5 text-ink-soft",
};

function NodeRow({
  node,
  depth,
  showUserLevel,
}: {
  node: TreeNode;
  depth: number;
  showUserLevel: boolean;
}) {
  const { t } = useTranslation();
  const hasChildren = node.children && node.children.length > 0;
  const [open, setOpen] = useState(depth === 0); // subjects open by default

  const lvl = node.user_level;

  return (
    <div>
      <div
        className={cx(
          "flex items-center gap-2 rounded-lg px-2 py-1.5",
          hasChildren && "cursor-pointer hover:bg-black/[0.03]",
        )}
        style={{ paddingLeft: `${depth * 14 + 8}px` }}
        onClick={hasChildren ? () => setOpen((v) => !v) : undefined}
      >
        <span className={cx("w-3 shrink-0 text-ink-faint transition-transform", open && "rotate-90")}>
          {hasChildren ? "▸" : ""}
        </span>
        <span
          className={cx(
            "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            KIND_COLOR[node.kind] || "bg-black/5 text-ink-soft",
          )}
        >
          {t(node.kind)}
        </span>
        <span className="min-w-0 flex-1 truncate font-medium text-ink">{node.title}</span>

        {/* Global comparison — hidden for personal sub-floor nodes (user_count 0). */}
        {node.user_count > 0 && (
          <span className="hidden shrink-0 items-center gap-3 text-xs text-ink-faint sm:flex">
            <span>
              <b className="font-semibold text-ink-soft">{node.user_count.toLocaleString()}</b>{" "}
              {t("learners", { count: node.user_count })}
            </span>
            {node.accuracy != null && <span>{t("{{pct}} correct", { pct: pct(node.accuracy) })}</span>}
            {node.mean_theta_percentile != null && <span>~{pct100(node.mean_theta_percentile)}ile</span>}
          </span>
        )}

        {showUserLevel && (
          <span className="shrink-0 text-xs">
            {lvl && (lvl.percentile != null || lvl.mastery != null) ? (
              <span className="rounded-md bg-brand-50 px-2 py-0.5 font-semibold text-brand-700">
                {t("you:")} {lvl.percentile != null ? `${Math.round(lvl.percentile)}ile` : "—"}
                {lvl.mastery != null ? ` · ${pct(lvl.mastery)}` : ""}
              </span>
            ) : lvl && lvl.attempts > 0 ? (
              <span className="rounded-md bg-black/5 px-2 py-0.5 font-medium text-ink-soft">
                {t("{{answered}} answered", { answered: lvl.attempts.toLocaleString() })}
              </span>
            ) : (
              <span className="text-ink-faint/60">—</span>
            )}
          </span>
        )}
      </div>

      {hasChildren && open && (
        <div className="border-l border-black/5" style={{ marginLeft: `${depth * 14 + 14}px` }}>
          {node.children.map((c) => (
            <NodeRow key={c.node_id} node={c} depth={depth + 1} showUserLevel={showUserLevel} />
          ))}
        </div>
      )}
    </div>
  );
}

export function TreeView({
  nodes,
  showUserLevel = false,
  emptyMessage,
}: {
  nodes: TreeNode[];
  showUserLevel?: boolean;
  emptyMessage?: string;
}) {
  const { t } = useTranslation();
  if (!nodes || nodes.length === 0) {
    return (
      <p className="rounded-xl bg-black/[0.02] px-4 py-6 text-center text-sm text-ink-faint">
        {emptyMessage ?? t("No topics have cleared the 3-learner threshold yet — check back soon.")}
      </p>
    );
  }
  return (
    <div className="card p-2">
      {nodes.map((n) => (
        <NodeRow key={n.node_id} node={n} depth={0} showUserLevel={showUserLevel} />
      ))}
    </div>
  );
}
